/**
 * Pure scaled-space maths (M3 plan R8), tested. The scaled camera sits at the scaled scene's origin; every body is
 * placed at (body − camera) × scale, subtracted in float64 BEFORE the float32 cast: the D-011 rebasing rule at
 * 1:1000. Everything here is allocation-free (it runs per body per frame).
 */
import type { Vec3 } from '../../core/math'
import type { ScaledConfig } from './config'

const TAU = Math.PI * 2

/**
 * Scaled-space position of a float64 world point for the float64 camera `cam`: (p − cam) in float64, then × scale,
 * then cast to float32. Writes `out` (usually an Object3D.position) and returns it. Allocation-free.
 */
export function rebaseScaled(x: number, y: number, z: number, cam: Readonly<Vec3>, scale: number, out: Vec3): Vec3 {
  out.x = Math.fround((x - cam.x) * scale)
  out.y = Math.fround((y - cam.y) * scale)
  out.z = Math.fround((z - cam.z) * scale)
  return out
}

/** Angular radius (radians) of a sphere of `radius` whose centre is `distance` away (π/2 from inside it). */
export function angularRadius(radius: number, distance: number): number {
  if (distance <= radius) return Math.PI / 2
  return Math.asin(radius / distance)
}

/**
 * Minimum on-screen size (D-TBD game-scale planets): the factor a body is scaled by so its angular radius is at least
 * `minAngularRadiusRad` (1 when it already is, or when the minimum is 0).
 */
export function minSizeBoost(angularRadiusRad: number, minAngularRadiusRad: number): number {
  return angularRadiusRad > 0 && angularRadiusRad < minAngularRadiusRad ? minAngularRadiusRad / angularRadiusRad : 1
}

/** The atmosphere shell scale for a boost: the look's scale at 1, ramping to `glowScale` by a boost of 2. */
export function glowShellScale(lookScale: number, glowScale: number, boost: number): number {
  const t = Math.min(1, Math.max(0, boost - 1))
  return lookScale + (Math.max(lookScale, glowScale) - lookScale) * t
}

/** Index into `segments` for an angular radius: the first threshold it is below, else the last entry. */
export function lodIndex(angularRadiusRad: number, thresholdsRad: readonly number[]): number {
  for (let i = 0; i < thresholdsRad.length; i++) if (angularRadiusRad < thresholdsRad[i]) return i
  return thresholdsRad.length
}

/** Sphere width segments for an angular radius (the LOD table). */
export function sphereSegmentsFor(angularRadiusRad: number, lod: ScaledConfig['lod']): number {
  return lod.segments[lodIndex(angularRadiusRad, lod.thresholdsRad)]
}

/** The star's point-light intensity: base × clamp(luminosity ^ exponent, minScale, maxScale). */
export function starLightIntensity(light: ScaledConfig['light'], luminosity: number): number {
  const s = Math.pow(Math.max(luminosity, 0), light.luminosityExponent)
  return light.intensity * Math.min(Math.max(s, light.minScale), light.maxScale)
}

/** Ring inner and outer radii in scaled units for a body of `radiusM` metres. */
export function ringRadii(radiusM: number, scale: number, rings: ScaledConfig['rings']): { inner: number; outer: number } {
  const r = radiusM * scale
  return { inner: r * rings.inner, outer: r * rings.outer }
}

/** Atmosphere shell radius in scaled units for a body of `radiusM` metres. */
export function atmosphereRadius(radiusM: number, scale: number, shellScale: number): number {
  return radiusM * scale * shellScale
}

/**
 * Spin angle (radians, in [0, 2π)) after `timeS` for a rotation period (seconds; negative = retrograde, 0 = no
 * spin). Wraps the turn count in float64 first so the angle stays precise over long sessions.
 */
export function spinAngle(timeS: number, periodS: number): number {
  if (periodS === 0 || !Number.isFinite(periodS)) return 0
  const turns = timeS / periodS
  const frac = turns - Math.floor(turns)
  return frac * TAU
}
